import { Image } from '@/components/Image';
import useApproximateProgress from '@/hooks/useApproximateProgress';
import { ICardMediaAsset, MediaAssetStatus, PlayAppMediaAssetStatus, MediaAssetType } from '@/types/media';
import { getOutputBucketUrl } from '@/utils/public-urls';
import React, { memo, useCallback } from 'react';
import ErrorDisplay from '../videos/ErrorDisplay';
import ProgressBar from '../videos/ProgressBar';
import { BlockStack, Box, Icon, Text } from '@shopify/polaris';
import { useUI } from '@/providers/UIProvider';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
interface MediaAssetCardContentProps {
  media: ICardMediaAsset;
  size?: 'small' | 'medium' | 'large';
  inGrid?: boolean;
  showProgressBannerWarning?: boolean;
}

const Media = ({ media, size = 'medium', inGrid = false }: MediaAssetCardContentProps) => {
  const { setMediaAsset } = useUI();
  const src = media.src || `${getOutputBucketUrl()}/${media.result}`;

  const handleImageClick = useCallback(
    (event) => {
      if (event.target instanceof HTMLImageElement) {
        setMediaAsset({
          ...event.target,
          src,
          type: MediaAssetType.image,
        });
      }
    },
    [src, setMediaAsset]
  );
  if (media.type === 'image') {
    return (
      <Image
        src={src}
        alt={media.name || media.alt || 'media asset'}
        fill
        objectFit="contain"
        size={size}
        inGrid={inGrid}
        quality={100}
        className="cursor-pointer"
        onClick={handleImageClick}
      />
    );
  } else if (media.type === 'video') {
    return (
      <video
        className="absolute inset-0 aspect-[9/16] h-full object-contain [&::-webkit-media-controls-fullscreen-button]:hidden [&::-webkit-media-controls-fullscreen-button]:!appearance-none"
        controls
        controlsList="nofullscreen nodownload noremoteplayback noplaybackspeed noplaybackrate"
        disablePictureInPicture
        poster={media.previewImage}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        preload={media.previewImage ? 'metadata' : 'auto'}
      >
        <source src={`${src}${media.previewImage ? '' : '#t=0.1'}`} />
      </video>
    );
  }

  return null;
};

const MediaAssetCardContent: React.FC<MediaAssetCardContentProps> = ({
  media,
  size,
  inGrid,
  showProgressBannerWarning = false,
}) => {
  const progress = useApproximateProgress(60, media.createdAt);

  if (media.status === MediaAssetStatus.FAILED) {
    return <ErrorDisplay message="Generation failed" />;
  }

  if (
    [MediaAssetStatus.PROCESSING, PlayAppMediaAssetStatus.Processing, PlayAppMediaAssetStatus.Uploading].includes(
      media.status as MediaAssetStatus | PlayAppMediaAssetStatus
    )
  ) {
    return (
      <>
        {showProgressBannerWarning ? (
          <div className="flex h-full w-full items-center justify-between">
            <BlockStack gap="800">
              <ProgressBar progress={progress} />
              <Box paddingInlineStart="400" paddingInlineEnd="400">
                <Text as="p" variant="bodyXs" tone="caution">
                  <Icon source={AlertTriangleIcon} /> Due to increased demand, generations may take a little longer.
                </Text>
              </Box>
            </BlockStack>
          </div>
        ) : (
          <ProgressBar progress={progress} />
        )}
      </>
    );
  }

  return (
    <div className="relative h-full w-full bg-black">
      <Media media={media} size={size} inGrid={inGrid} />
    </div>
  );
};

export default memo(MediaAssetCardContent);
